import { Badge } from "@shared/components/ui/badge";
import { Card } from "@shared/components/ui/card";
import { Separator } from "@shared/components/ui/separator";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { format } from "date-fns";
import {
  AlertCircle,
  CalendarIcon,
  CheckCircle2,
  Clock,
  MapPin,
  NotepadText,
  Phone,
  Store,
  User,
  Receipt,
  Package,
  AlertTriangle,
  Info,
  Eye,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ReturnVoucherAction from "./ReturnVoucherAction";
import { Link } from "react-router-dom";
import { Button } from "@shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/components/ui/tooltip";

const statusIcons = {
  approved: <CheckCircle2 className="size-4 text-green-500" />,
  pending: <Clock className="size-4 text-yellow-500" />,
  rejected: <AlertCircle className="size-4 text-red-500" />,
  paid: <CheckCircle2 className="size-4 text-green-500" />,
  canceled: <AlertCircle className="size-4 text-red-500" />,
  return_collection: <Package className="size-4 text-purple-500" />,
};

const statusColors = {
  approved: "bg-green-50 text-green-700 border-green-200",
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  canceled: "bg-red-50 text-red-700 border-red-200",
  return_collection: "bg-purple-50 text-purple-700 border-purple-200",
};

const getInvoiceStats = (packages) => {
  const invoicedPackages =
    packages?.filter((pkg) => pkg.invoiced || pkg.invoice) || [];
  const uninvoicedPackages =
    packages?.filter((pkg) => !pkg.invoiced && !pkg.invoice) || [];
  return {
    total: packages?.length || 0,
    invoiced: invoicedPackages.length,
    uninvoiced: uninvoicedPackages.length,
    invoicedPackages,
    uninvoicedPackages,
  };
};

const ReturnVoucherCard = ({ voucher, compact = false }) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const invoiceStats = getInvoiceStats(voucher.packages);

  // Compact view
  if (compact) {
    return (
      <Card className="w-full shadow-sm transition-all duration-200 hover:shadow-md border-l-4 border-l-blue-500">
        <div className="p-3 space-y-2">
          {/* Compact Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              <Receipt className="size-4 text-blue-600 flex-shrink-0" />
              <Link to={`/return-voucher/${voucher._id}`}>
                <h3 className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors truncate">
                  {voucher._id}
                </h3>
              </Link>
            </div>
            <Badge
              variant="outline"
              className={`${
                statusColors[voucher.status]
              } font-medium text-xs flex-shrink-0`}
            >
              {statusIcons[voucher.status]}
              <span className="ml-1">{t(`status.${voucher.status}`)}</span>
            </Badge>
          </div>

          {/* Compact Info Row */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-gray-600">
                <Package className="size-3" />
                <span className="font-medium">{voucher.packages.length}</span>
              </div>
              {auth.isAdmin && (
                <div className="flex items-center gap-1 text-gray-600">
                  <Store className="size-3" />
                  <span className="font-medium truncate max-w-[100px]">
                    {voucher.client.brandName}
                  </span>
                </div>
              )}
            </div>

            {/* Compact Financial Summary */}
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="font-bold text-green-700">
                  {voucher.totalNet} MAD
                </div>
                <div className="text-gray-500">{t("common.net")}</div>
              </div>
            </div>
          </div>

          {/* Invoice Status Indicator */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-green-600 font-medium">
                  {invoiceStats.invoiced}
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">{invoiceStats.total}</span>
                <span className="text-gray-500">{t("invoices.invoiced")}</span>
              </div>
              {invoiceStats.uninvoiced > 0 && (
                <AlertTriangle className="size-3 text-orange-600" />
              )}
            </div>

            <div className="flex items-center gap-1">
              <ReturnVoucherAction
                returnVoucherId={voucher._id}
                voucher={voucher}
                showPDFActions={true}
                showUpdateStatus={false}
              />
              <Link to={`/return-voucher/${voucher._id}`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 text-xs hover:text-blue-600"
                >
                  <Eye className="size-3 mr-1" />
                  {t("common.viewDetails")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Default full view
  return (
    <Card className="w-full shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border-l-4 border-l-blue-500">
      <div className="p-4 space-y-4">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          {auth.isAdmin ? (
            <>
              <div className="flex items-center gap-2">
                <MapPin className="size-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {voucher.client.address} - {voucher.client.city}
                </h3>
              </div>
              <ReturnVoucherAction
                returnVoucherId={voucher._id}
                voucher={voucher}
                showPDFActions={true}
                showUpdateStatus={false}
              />
            </>
          ) : (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Receipt className="size-4 text-blue-600" />
                <Link to={`/return-voucher/${voucher._id}`}>
                  <h3 className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                    #{voucher._id.substring(0, 8)}...
                  </h3>
                </Link>
              </div>
              <ReturnVoucherAction
                returnVoucherId={voucher._id}
                voucher={voucher}
                showPDFActions={true}
                showUpdateStatus={false}
              />
            </div>
          )}
        </div>

        {/* Status and Package Count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge
              variant="outline"
              className={`${statusColors[voucher.status]} font-medium`}
            >
              {statusIcons[voucher.status]}
              <span className="ml-1">{t(`status.${voucher.status}`)}</span>
            </Badge>

            <div className="flex items-center gap-1 text-sm text-gray-600">
              <Package className="size-4" />
              <span className="font-medium">{voucher.packages.length}</span>
              <span>{t("packages.packages")}</span>
            </div>
          </div>
        </div>

        {/* Invoice Status Section */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Receipt className="size-4" />
              {t("invoices.invoiceStatus")}
            </h4>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="size-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">
                    {t("invoices.invoiceStatusTooltip")}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="text-center p-2 bg-white rounded border">
              <div className="font-bold text-gray-700">
                {invoiceStats.total}
              </div>
              <div className="text-gray-500">{t("common.total")}</div>
            </div>
            <div className="text-center p-2 bg-green-50 rounded border border-green-200">
              <div className="font-bold text-green-700">
                {invoiceStats.invoiced}
              </div>
              <div className="text-green-600">{t("invoices.invoiced")}</div>
            </div>
            <div className="text-center p-2 bg-orange-50 rounded border border-orange-200">
              <div className="font-bold text-orange-700">
                {invoiceStats.uninvoiced}
              </div>
              <div className="text-orange-600">{t("invoices.pending")}</div>
            </div>
          </div>

          {/* Warning for uninvoiced packages */}
          {invoiceStats.uninvoiced > 0 && (
            <div className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200">
              <AlertTriangle className="size-4 text-orange-600" />
              <span className="text-xs text-orange-700">
                {invoiceStats.uninvoiced} {t("packages.packages")}{" "}
                {t("invoices.awaitingInvoice")}
              </span>
            </div>
          )}
        </div>

        {/* Client Information */}
        {auth.isAdmin && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="size-4" />
                <span className="font-medium">{voucher.client.phone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Store className="size-4" />
                <span className="font-medium">{voucher.client.brandName}</span>
              </div>
            </div>
          </div>
        )}

        {/* Note Section */}
        {voucher.note && (
          <div className="flex items-start gap-2 p-2 bg-blue-50 rounded border border-blue-200">
            <NotepadText className="size-4 text-blue-600 mt-0.5" />
            <p className="text-xs text-blue-700 flex-1">{voucher.note}</p>
          </div>
        )}

        {/* Financial Summary */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center p-2 bg-gray-50 rounded">
            <div className="font-bold text-gray-700">
              {voucher.totalCost} MAD
            </div>
            <div className="text-gray-500">{t("common.cost")}</div>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded">
            <div className="font-bold text-blue-700">
              {voucher.totalAmount} MAD
            </div>
            <div className="text-blue-600">{t("common.amount")}</div>
          </div>
          <div className="text-center p-2 bg-green-50 rounded">
            <div className="font-bold text-green-700">
              {voucher.totalNet} MAD
            </div>
            <div className="text-green-600">{t("common.net")}</div>
          </div>
        </div>

        <Separator />

        {/* Footer Information */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <CalendarIcon className="size-3" />
            <span>
              {t("common.created")}:{" "}
              {format(voucher.createdAt, "dd/MM/yyyy HH:mm")}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Link to={`/return-voucher/${voucher._id}`}>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-1 text-xs hover:text-blue-600"
              >
                <Eye className="size-3 mr-1" />
                {t("common.viewDetails")}
              </Button>
            </Link>

            {auth.isAdmin && (
              <Link to={`/users/${voucher.user._id}`}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1 text-xs"
                >
                  <User className="size-3 mr-1" />
                  {voucher.user.brandName}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ReturnVoucherCard;
